'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/src/shared/lib/utils';
import type { ChatFilter, ConnectionStatus } from '../types';

const FILTER_OPTIONS: { value: ChatFilter; label: string }[] = [
  { value: 'All', label: 'All' },
  { value: 'Active', label: 'Current Orders' },
  { value: 'Completed', label: 'Completed Orders' },
  { value: 'Unread', label: 'Unread' },
];

interface ChatHeaderProps {
  title?: string;
  showFilter?: boolean;
  filterValue?: ChatFilter;
  onFilterChange?: (filter: ChatFilter) => void;
  onBack?: () => void;
  onCollapse?: () => void;
  onCreateOrder?: () => void;
  showCreateOrder?: boolean;
  connectionStatus?: ConnectionStatus;
  className?: string;
}

export const ChatHeader = ({
  title = 'Orders',
  showFilter = true,
  filterValue = 'All',
  onFilterChange,
  onBack,
  onCollapse,
  onCreateOrder,
  showCreateOrder = false,
  connectionStatus,
  className,
}: ChatHeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedLabel =
    FILTER_OPTIONS.find((o) => o.value === filterValue)?.label ?? 'All';

  const handleSelect = (value: ChatFilter) => {
    onFilterChange?.(value);
    setIsOpen(false);
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between',
        'px-3 py-5',
        'bg-background-primary',
        className
      )}
    >
      {/* Left side - Title */}
      <div className="flex items-center gap-5">
        {onBack && (
          <button
            onClick={onBack}
            className="p-0 hover:opacity-70 transition-opacity"
            aria-label="Back"
          >
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path
                d="M30 36L18 24L30 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-text-primary"
              />
            </svg>
          </button>
        )}

        {title && (
          <span className="text-text-primary text-4xl font-bold font-['Roboto'] leading-10">
            {title}
          </span>
        )}

        {/* Connection status indicator */}
        {connectionStatus && connectionStatus !== 'connected' && (
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                connectionStatus === 'connecting' && 'bg-yellow-400 animate-pulse',
                connectionStatus === 'disconnected' && 'bg-red-400'
              )}
            />
            <span className="text-text-secondary text-sm">
              {connectionStatus === 'connecting' && 'Connecting...'}
              {connectionStatus === 'disconnected' && 'Disconnected'}
            </span>
          </div>
        )}
      </div>

      {/* Right side - Create Order button + Filter dropdown */}
      <div className="flex items-center gap-2.5">
        {showCreateOrder && onCreateOrder && (
          <button
            onClick={onCreateOrder}
            className={cn(
              'h-12 px-5 py-3 rounded-full',
              'bg-[#6AB344]',
              'inline-flex justify-center items-center gap-2',
              'hover:bg-[#5A9B38] transition-colors'
            )}
            aria-label="Create Order"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 4V16M4 10H16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-white text-base font-medium font-['Roboto'] leading-5">
              Create Order
            </span>
          </button>
        )}

        {showFilter && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen((prev) => !prev)}
              className={cn(
                'w-80 h-12 pl-5 pr-3 py-3 rounded-full',
                'bg-background-primary',
                'shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)]',
                'border border-accent-secondary',
                'inline-flex justify-between items-center',
                'hover:bg-background-secondary/30 transition-colors'
              )}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            >
              <span className="text-text-primary text-base font-normal font-['Roboto'] leading-5">
                {selectedLabel}
              </span>
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                className={cn('transition-transform duration-200', isOpen && 'rotate-180')}
              >
                <path
                  d="M8 12L16 20L24 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-text-primary"
                />
              </svg>
            </button>

            {isOpen && (
              <div
                className={cn(
                  'absolute right-0 top-full mt-1 z-50',
                  'w-80 py-1 rounded-2xl',
                  'bg-background-primary',
                  'border border-accent-secondary',
                  'shadow-lg'
                )}
                role="listbox"
              >
                {FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    role="option"
                    aria-selected={filterValue === option.value}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      'w-full text-left px-5 py-3',
                      'text-base font-normal font-[\'Roboto\'] leading-5',
                      'hover:bg-background-secondary/40 transition-colors',
                      filterValue === option.value
                        ? 'text-text-primary font-medium'
                        : 'text-text-secondary'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
